import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { AxiosResponse } from 'axios'
import { Asset, AssetSecurityInfo } from '@/entities/assets/utils/types'
import { ApiListResponse } from '../utils/types'
import { DuckDBSchemaType } from '@types'

import { ASSET_FIELD } from '@/const'

import api from '@/store/api'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  updateStoreListItem,
} from '../utils/helpers'
import { isItCommotType } from './utils/helpers'

import { useDuckDB } from '@/services/duckdb'

import useAnalyticsStore from '@/store/analytics'
import { useRepositoriesStore } from '@/store/repositories/index'

export const TABLE_NAME = 'assets'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [ASSET_FIELD.ID]: 'VARCHAR',
  [ASSET_FIELD.NAME]: 'VARCHAR',
  [ASSET_FIELD.TICKER]: 'VARCHAR',
  [ASSET_FIELD.TYPE]: 'VARCHAR',
}

export const useAssetsStore = defineStore('assets', () => {
  // INIT
  const { pushData, createTable } = useDuckDB()

  const analyticsStore = useAnalyticsStore()
  const repositoriesStore = useRepositoriesStore()

  const list = ref<Asset[]>([])
  const types = ref<string[]>([])

  const initFlag = ref(false)
  const trigger = ref(0)

  const loading = ref(true)
  const loadingAction = ref(false)
  const loadingSecurityInfo = ref(false)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS
  const getList = computed(() => list.value)
  const getTypes = computed(() => types.value.filter(isItCommotType))

  // SETTERS

  const setList = async (data: Asset[]) => {
    list.value = data
    await createTable(TABLE_NAME, TABLE_SCHEMA)
    await pushData(TABLE_NAME, data, TABLE_SCHEMA)
    trigger.value++
  }

  const setTypes = (data: string[]) => {
    types.value = data
  }

  const pushAssetToList = (asset: Asset) => {
    addStoreListItem<Asset>(list.value, asset)
  }

  // ACTIONS

  const fetch = async () => {
    try {
      loading.value = true
      const result = await api.get<ApiListResponse<Asset[]>>('assets', {
        signal: abortController.signal,
        params: {
          repository_id: repositoryId.value,
        },
      })
      await setList(result.data.data)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      initFlag.value = true
      loading.value = false
    }
  }

  const fetchSingle = async (asset_id: string): Promise<Asset> => {
    try {
      const result = await api.get<Asset>(`assets/${asset_id}`, {
        signal: abortController.signal,
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const fetchTypes = async (): Promise<void> => {
    try {
      const result = await api.get<string[]>('asset_types', {
        signal: abortController.signal,
      })
      setTypes(result.data)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const fetchSecurityInfo = async (securityInfoId: string) => {
    loadingSecurityInfo.value = true
    try {
      const result = await api.get<AssetSecurityInfo>(
        `sec_master/${securityInfoId}`,
        {
          signal: abortController.signal,
        },
      )
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingSecurityInfo.value = false
    }
  }

  const store = async (asset: Asset): Promise<Asset> => {
    loadingAction.value = true
    try {
      const result = await api.post<Asset, AxiosResponse<Asset>, Asset>(
        'assets',
        asset,
      )
      const data = result.data
      addStoreListItem<Asset>(list.value, data)
      if (result.data.currency_asset) {
        pushAssetToList(result.data.currency_asset)
      }
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (asset: Partial<Asset>): Promise<Asset | undefined> => {
    loadingAction.value = true
    try {
      const { id } = asset
      const result = await api.put<Asset, AxiosResponse<Asset>, Partial<Asset>>(
        `assets/${id}`,
        asset,
      )
      const data = result.data
      updateStoreListItem<Asset>(list.value, data)
      if (result.data.currency_asset) {
        pushAssetToList(result.data.currency_asset)
      }
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: string, analyticsDeprecated = true): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete(`assets/${id}`)
      removeStoreListItem<Asset>(list.value, id)
      analyticsDeprecated && analyticsStore.markAsDeprecated()
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const bulkStore = async (assets: Partial<Asset>[]): Promise<Asset[]> => {
    loadingAction.value = true
    try {
      const result = await api.post<
        Asset[],
        AxiosResponse<Asset[]>,
        Partial<Asset>[]
      >(`assets?repository_id=${repositoryId.value}`, assets)
      result.data.forEach(asset => {
        addStoreListItem<Asset>(list.value, asset)
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const bulkUpdate = async (assets: Partial<Asset>[]): Promise<void> => {
    loadingAction.value = true
    try {
      await Promise.all(assets.map(asset => update(asset)))
    } catch (e) {
      throw Error(e as string)
    } finally {
      loadingAction.value = false
    }
  }

  const bulkDestroy = async (ids: string[]): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete('assets', { data: ids })
      ids.forEach(id => {
        removeStoreListItem<Asset>(list.value, id)
      })
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    setList([])
  }

  return {
    initFlag,
    trigger,

    loading,
    loadingAction,
    loadingSecurityInfo,

    getList,
    getTypes,

    pushAssetToList,

    fetch,
    fetchSingle,
    fetchTypes,
    fetchSecurityInfo,

    store,
    update,
    destroy,

    bulkStore,
    bulkUpdate,
    bulkDestroy,

    cancel,
    clear,
  }
})
